import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useBrand } from "@/contexts/BrandContext";
import {
  Calendar, MapPin, Users, Mail, Phone, User, MessageSquare,
  CheckCircle, Loader2, AlertCircle, ChevronDown
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function usePublicTenantConfig(alias) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!alias) return;
    axios.get(`${BASE}/public/tenant/${alias}`)
      .then(r => setConfig(r.data))
      .catch(e => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [alias]);

  return { config, loading, notFound };
}

const SERVICES_LIST = [
  "Venue", "Decor", "Catering", "Photography", "Entertainment",
  "Lighting", "Hospitality", "Transport",
];

export default function PublicInquiry() {
  const { alias } = useParams();
  const { config, loading, notFound } = usePublicTenantConfig(alias);

  const [form, setForm] = useState({
    name: "", mobile: "", email: "", event_type: "",
    event_date: "", location: "", guest_count: "", services: [],
    message: "", consent: false,
    _hp_field: "", // honeypot — must stay empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { reference }
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  const accent = config?.accent_color || "#0ea5e9";

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  function toggleService(svc) {
    setForm(f => {
      const svcs = f.services.includes(svc)
        ? f.services.filter(s => s !== svc)
        : [...f.services, svc];
      return { ...f, services: svcs };
    });
  }

  function validate() {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = "Full name required";
    if (!form.mobile.trim() || form.mobile.replace(/\D/g, "").length < 7) errs.mobile = "Valid mobile number required";
    if (!form.email.trim() || !form.email.includes("@")) errs.email = "Valid email required";
    if (!form.consent) errs.consent = "Please accept to continue";
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError("");
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${BASE}/public/inquire/${alias}`, {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim().toLowerCase(),
        event_type: form.event_type || null,
        event_date: form.event_date || null,
        location: form.location.trim() || null,
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        services: form.services,
        message: form.message.trim() || null,
        consent: form.consent,
        _hp_field: form._hp_field,
      });
      setSubmitted(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  // ── Not found / unavailable ──────────────────────────────────────────────
  if (notFound || !config) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <AlertCircle className="w-6 h-6 text-gray-400" />
        </div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Form Not Available</h1>
        <p className="text-gray-500 text-sm max-w-sm">
          This inquiry form is not available. Please contact the organiser directly.
        </p>
        <Attribution />
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
          style={{ backgroundColor: `${accent}15` }}
        >
          <CheckCircle className="w-7 h-7" style={{ color: accent }} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
        <p className="text-gray-600 mb-1">{config.contact_context}</p>
        <p className="text-sm text-gray-400 mt-3">
          Reference: <span className="font-mono font-medium text-gray-600">{submitted.reference}</span>
        </p>
        <div
          className="mt-8 px-5 py-3 rounded-full text-sm font-medium text-white cursor-pointer"
          style={{ backgroundColor: accent }}
          onClick={() => { setSubmitted(null); setForm({ name:"",mobile:"",email:"",event_type:"",event_date:"",location:"",guest_count:"",services:[],message:"",consent:false,_hp_field:"" }); }}
        >
          Submit another inquiry
        </div>
        <Attribution className="mt-10" />
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  const eventTypeOptions = config.event_types?.length
    ? config.event_types
    : [
        { value: "ET-WEDDING", label: "Wedding" },
        { value: "ET-ENGAGEMENT", label: "Engagement" },
        { value: "ET-RECEPTION", label: "Reception" },
        { value: "ET-CORPORATE", label: "Corporate Event" },
      ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero header */}
      <div className="w-full pt-10 pb-8 px-6 text-center" style={{ borderBottom: "1px solid #e5e7eb" }}>
        <div className="max-w-sm mx-auto">
          {config.logo_url && (
            <img src={config.logo_url} alt={config.display_name} className="h-10 mx-auto mb-4 object-contain" />
          )}
          <h1
            className="text-2xl font-bold tracking-tight text-gray-900 mb-1"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {config.display_name}
          </h1>
          <p className="text-gray-500 text-sm leading-relaxed">{config.tagline}</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 pt-6 pb-16" noValidate>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{config.form_title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{config.contact_context}</p>
          </div>

          {/* Honeypot (hidden from humans, filled by bots) */}
          <input
            type="text"
            name="_hp_field"
            value={form._hp_field}
            onChange={e => set("_hp_field", e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
          />

          {/* Name */}
          <Field label="Full Name" required error={errors.name} icon={<User className="w-4 h-4" />}>
            <input
              data-testid="public-inquiry-name"
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              className={inputCls(errors.name)}
            />
          </Field>

          {/* Mobile */}
          <Field label="Mobile Number" required error={errors.mobile} icon={<Phone className="w-4 h-4" />}>
            <input
              data-testid="public-inquiry-mobile"
              type="tel"
              placeholder="+91 98765 43210"
              value={form.mobile}
              onChange={e => set("mobile", e.target.value)}
              className={inputCls(errors.mobile)}
            />
          </Field>

          {/* Email */}
          <Field label="Email Address" required error={errors.email} icon={<Mail className="w-4 h-4" />}>
            <input
              data-testid="public-inquiry-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => set("email", e.target.value)}
              className={inputCls(errors.email)}
            />
          </Field>

          {/* Event Type */}
          <Field label="Event Type" icon={<Calendar className="w-4 h-4" />}>
            <div className="relative">
              <select
                data-testid="public-inquiry-event-type"
                value={form.event_type}
                onChange={e => set("event_type", e.target.value)}
                className={`${inputCls()} appearance-none pr-8`}
              >
                <option value="">Select event type</option>
                {eventTypeOptions.map(et => (
                  <option key={et.value} value={et.value}>{et.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </Field>

          {/* Date + Location row */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Event Date" icon={<Calendar className="w-4 h-4" />}>
              <input
                data-testid="public-inquiry-event-date"
                type="date"
                value={form.event_date}
                onChange={e => set("event_date", e.target.value)}
                className={inputCls()}
                min={new Date().toISOString().split("T")[0]}
              />
            </Field>
            <Field label="Guest Count" icon={<Users className="w-4 h-4" />}>
              <input
                data-testid="public-inquiry-guest-count"
                type="number"
                placeholder="e.g. 500"
                value={form.guest_count}
                onChange={e => set("guest_count", e.target.value)}
                className={inputCls()}
                min="1"
              />
            </Field>
          </div>

          {/* Location */}
          <Field label="Location / City" icon={<MapPin className="w-4 h-4" />}>
            <input
              data-testid="public-inquiry-location"
              type="text"
              placeholder="e.g. Hyderabad"
              value={form.location}
              onChange={e => set("location", e.target.value)}
              className={inputCls()}
            />
          </Field>

          {/* Services */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Services Needed
            </label>
            <div className="flex flex-wrap gap-2">
              {(config.service_categories?.length ? config.service_categories.map(s => s.label) : SERVICES_LIST).map(svc => {
                const sel = form.services.includes(svc);
                return (
                  <button
                    key={svc}
                    type="button"
                    data-testid={`service-tag-${svc.toLowerCase()}`}
                    onClick={() => toggleService(svc)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150"
                    style={sel ? {
                      backgroundColor: accent,
                      borderColor: accent,
                      color: "white",
                    } : {
                      backgroundColor: "white",
                      borderColor: "#e5e7eb",
                      color: "#6b7280",
                    }}
                  >
                    {svc}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <Field label="Tell us more" icon={<MessageSquare className="w-4 h-4" />}>
            <textarea
              data-testid="public-inquiry-message"
              rows={3}
              placeholder="Any specific requirements, budget range, venue preferences..."
              value={form.message}
              onChange={e => set("message", e.target.value)}
              className={`${inputCls()} resize-none`}
            />
          </Field>

          {/* Consent */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="public-inquiry-consent"
                type="checkbox"
                checked={form.consent}
                onChange={e => set("consent", e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-sky-500 flex-shrink-0"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                I agree to be contacted by {config.display_name} regarding my event inquiry.
                My information will not be shared with third parties.
              </span>
            </label>
            {errors.consent && (
              <p className="text-[#BA0517] text-xs mt-1 ml-7">{errors.consent}</p>
            )}
          </div>

          {/* Server error */}
          {serverError && (
            <div
              data-testid="public-inquiry-error"
              className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {serverError}
            </div>
          )}

          {/* Submit */}
          <button
            data-testid="public-inquiry-submit"
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
            style={{ backgroundColor: accent }}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Submitting…" : config.cta_text}
          </button>
        </div>

        <Attribution className="mt-8" />
      </form>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Field({ label, required, error, icon, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
        {label}{required && <span className="text-[#BA0517] ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-[#BA0517] text-xs mt-1">{error}</p>}
    </div>
  );
}

function inputCls(error) {
  return [
    "w-full px-3 py-2.5 rounded-xl border text-sm text-gray-800 bg-white",
    "placeholder-gray-400 outline-none transition-all duration-150",
    "focus:ring-2 focus:ring-sky-200 focus:border-sky-400",
    error ? "border-red-300 bg-red-50" : "border-gray-200",
  ].join(" ");
}

function Attribution({ className = "" }) {
  const brand = useBrand();
  return (
    <div className={`text-center space-y-0.5 ${className}`}>
      <p className="text-[10px] text-gray-300 font-medium tracking-widest uppercase">
        Powered by {brand.productName}
      </p>
      <p className="text-[9px] text-gray-300 tracking-wide">
        {brand.productDescriptor}
      </p>
      <p className="text-[9px] text-gray-300 font-medium tracking-widest uppercase">
        {brand.companyName}
      </p>
    </div>
  );
}
